import { Star } from 'lucide-react'
import type { EmployeeProfile } from '@/types'
import { mockEmployees } from '@/data/mock-data'
import { cn } from '@/lib/utils'

interface RelatedEmployeesProps {
  employee: EmployeeProfile
}

/**
 * Related Employees Component - Microsoft Store Style
 * 
 * Simple sidebar showing related apps/employees with:
 * - Clean app cards with icons, ratings, and prices
 * - Minimal design matching Microsoft Store
 */
export function RelatedEmployees({ employee }: RelatedEmployeesProps) {
  // Get similar employees (simplified algorithm)
  const getSimilarEmployees = () => {
    return mockEmployees
      .filter(emp => emp.id !== employee.id)
      .filter(emp => {
        // Similar if same category or has similar tags
        const sameCategory = emp.category.name === employee.category.name
        const hasSimilarTags = employee.tags.some(tag => 
          emp.name.toLowerCase().includes(tag.toLowerCase()) ||
          emp.description.toLowerCase().includes(tag.toLowerCase())
        )
        return sameCategory || hasSimilarTags
      })
      .slice(0, 6)
  }

  const relatedEmployees = getSimilarEmployees()

  return (
    <div>
      {relatedEmployees.map((relatedEmployee) => (
        <div
          key={relatedEmployee.id}
          className="flex items-center gap-3 p-3 rounded-lg hover:bg-white cursor-pointer transition-colors"
          onClick={() => window.location.href = `/employee/${relatedEmployee.slug}`}
        >
          {/* App Icon */}
          <div className="w-12 h-12 rounded overflow-hidden bg-white border shadow-sm flex-shrink-0">
            <div className="w-full h-full bg-muted flex items-center justify-center text-lg">
              {relatedEmployee.icon}
            </div>
          </div>
          
          {/* App Info */}
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-sm truncate">
              {relatedEmployee.name}
            </h4>
            <div className="flex items-center gap-1 mb-1">
              <div className="flex items-center">
                {Array.from({ length: 5 }, (_, i) => (
                  <Star
                    key={i}
                    className={cn(
                      "w-3 h-3",
                      i < Math.floor(4.5) // Mock rating
                        ? "text-yellow-400 fill-yellow-400"
                        : "text-gray-300"
                    )}
                  />
                ))}
              </div>
              <span className="text-xs text-muted-foreground">4.5</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {relatedEmployee.category.name}
              </span>
              <span className="text-xs font-medium text-blue-600">
                Free
              </span>
            </div>
          </div>
        </div>
      ))}
      
      {relatedEmployees.length === 0 && (
        <div className="text-center py-6 text-sm text-muted-foreground">
          No related employees found
        </div>
      )}
    </div>
  )
}

RelatedEmployees.displayName = "RelatedEmployees"